import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Spinner from "../../../../../components/Spinner";
import DatabaseOffIco from "../../../../../components/icons/DatabaseOffIco";
import { useAuth } from "../../../../../providers/AuthContext";
import { useDashboards } from "../../../../../providers/DashboardContext";
import { getDataWidget, getWidgetById } from "../../../../../services/apiWidgests";
import { connectToProcess } from "../../../../../services/sockets/processSocket";
import { useAppContext } from '../../../../../providers/AppContext';

const DATA_DISPLAY_OPTIONS = {
  lastValue: "Ultimo valor",
  add: "Suma",
  mean: "Promedio",
  max: "Maximo",
  min: "Minimo",
};

const MIN_CARD_WIDTH_PX = 220;

const parseCustomDate = (dateStr) => {
  const match = dateStr?.match(
    /^(\d{2})\/(\d{2})\/(\d{4})[\sT](\d{2}):(\d{2}):(\d{2})$/
  );

  if (!match) return new Date(dateStr);

  const [, dd, mm, yyyy, hh, min, ss] = match;
  return new Date(`${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`);
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return "";

  const past = parseCustomDate(dateStr);
  const now = new Date();
  let diff = Math.floor((now - past) / 1000);

  if (diff < 1) return "hace 1 segundo";

  const units = [
    { label: "año", plural: "años", seconds: 365 * 24 * 3600 },
    { label: "mes", plural: "meses", seconds: 30 * 24 * 3600 },
    { label: "dia", plural: "dias", seconds: 24 * 3600 },
    { label: "hora", plural: "horas", seconds: 3600 },
    { label: "minuto", plural: "minutos", seconds: 60 },
    { label: "segundo", plural: "segundos", seconds: 1 },
  ];

  const result = [];

  for (const unit of units) {
    const value = Math.floor(diff / unit.seconds);
    if (value > 0) {
      result.push(`${value} ${value === 1 ? unit.label : unit.plural}`);
      diff -= value * unit.seconds;
    }
    if (result.length === 2) break;
  }

  return `hace ${result.join(" ")}`;
};

const MetricInnerCard = ({ item }) => {
  if (item?.isGrouped) {
    return (
      <div className="flex h-full flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col">
          <span className="truncate text-sm font-semibold text-gray-700">{item.name}</span>
          <span className="text-xs text-gray-500">
            {DATA_DISPLAY_OPTIONS[item.dataDisplay] || "--"}
          </span>
        </div>

        {item.groups?.length ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2">
            {item.groups.map((group, index) => (
              <div
                key={`${group.group || "group"}-${index}`}
                className="flex flex-col items-center justify-center rounded-md border border-gray-200 bg-gray-50 p-2"
              >
                <span className="text-xs font-semibold text-gray-600">{group.group}</span>
                <span className="text-center text-lg font-bold text-bluesens">
                  {group.idealValue != null
                    ? `${group.value}/${group.idealValue}`
                    : `${group.value ?? "--"}`}{" "}
                  {group.units || ""}
                </span>
                <span className="text-xs text-gray-500">{formatTimeAgo(group.lastUpdated)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
            No hay datos por el momento
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col justify-center gap-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col items-center text-center">
        <span className="truncate text-sm font-semibold text-gray-700">{item.name}</span>
        <span className="text-xs text-gray-500">
          {DATA_DISPLAY_OPTIONS[item.dataDisplay] || "--"}
        </span>
      </div>

      <div className="flex flex-col items-center gap-1">
        <span className="text-center text-3xl font-bold text-bluesens">
          {item.value ?? "--"} {item.units || ""}
        </span>

        {item.idealValue != null && (
          <span className="text-center text-sm font-semibold text-gray-500">
            Objetivo: <span className="text-black">{item.value}</span> / Ideal:{" "}
            <span className="text-blue-600">{item.idealValue}</span>{" "}
            {item.units || ""}
          </span>
        )}

        <span className="text-sm font-semibold text-gray-600">
          {formatTimeAgo(item.lastUpdated)}
        </span>
      </div>
    </div>
  );
};

const MultiMetricWidget = ({ id }) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { selectedDatarange } = useDashboards();
  const [fetchingWidgetData, setFetchingWidgetData] = useState(false);
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);

  const { addToast } = useAppContext();

  const refreshWidgetInfo = async () => {
    try {
      const response = await getWidgetById(id, auth?.token);
      setConfig(response?.configuration || {});
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetData !== 'undefined') setFetchingWidgetData(false);
        return;
      }
      console.error("Error cargando widget config:", error);
    }
  };

  const refreshWidgetData = async (withSpinner = true) => {
    try {
      if (withSpinner) {
        setFetchingWidgetData(true);
      }

      const response = await getDataWidget(id, {}, auth?.token);
      setItems(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetData !== 'undefined') setFetchingWidgetData(false);
        return;
      }
      if (error?.response?.status === 404) {
        console.log("Widget no encontrado");
      } else if (error?.response?.status === 500) {
        console.log("Error en el servidor");
      } else {
        navigate("/logout");
      }
    } finally {
      if (withSpinner) {
        setFetchingWidgetData(false);
      }
    }
  };

  useEffect(() => {
    refreshWidgetInfo();
    refreshWidgetData();
  }, [selectedDatarange, id]);

  useEffect(() => {
    if (!config?.metrics || !Array.isArray(config.metrics)) return;

    const processIds = config.metrics
      .map(m => m.process?.id)
      .filter((id, index, self) => id && self.indexOf(id) === index);

    if (processIds.length === 0) return;

    const disconnects = processIds.map(procId =>
      connectToProcess(
        procId,
        process.env.REACT_APP_SOCKET_URL,
        () => refreshWidgetData(false)
      )
    );

    return () => disconnects.forEach(disconnect => disconnect && disconnect());
  }, [config?.metrics]);

  const { fullWidthItems, regularItems } = useMemo(() => {
    const allItems = Array.isArray(items) ? items : [];
    return {
      fullWidthItems: allItems.filter((item) => item.fullWidth),
      regularItems: allItems.filter((item) => !item.fullWidth),
    };
  }, [items]);

  if (fetchingWidgetData) {
    return (
      <div className="flex h-full w-full justify-center p-4">
        <Spinner />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4">
        <span className="text-center text-lg text-gray-700">No hay datos por el momento</span>
        <DatabaseOffIco width="40px" height="40px" fill="gray" />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-3 overflow-auto bg-white p-3">
      {fullWidthItems.map((item, index) => (
        <div
          key={`${item.name || "metric"}-full-${index}`}
          className="w-full"
          style={{ minWidth: `${MIN_CARD_WIDTH_PX}px` }}
        >
          <MetricInnerCard item={item} />
        </div>
      ))}

      {regularItems.length > 0 && (
        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns: `repeat(auto-fit, minmax(${MIN_CARD_WIDTH_PX}px, 1fr))`,
          }}
        >
          {regularItems.map((item, index) => (
            <div key={`${item.name || "metric"}-regular-${index}`} className="min-h-[12rem]">
              <MetricInnerCard item={item} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiMetricWidget;
